import merge from 'deepmerge';
import queryString from 'query-string';
import { errorLog } from '../../utils/logUtils';
import { DEVICES } from '../../utils/constants';

/**
 * giveRandomNumber: calculate random number from min to max both limits included
 * @param {number} min min number you could get
 * @param {number} max max number you could get
 * @returns {number} a random number
 */
export const giveRandomNumber = (min: number, max: number): number =>
  Math.floor(Math.random() * max) + min;

/**
 * generateComponentId:
 * @param componentName generate componnntId string using componentName, aditional id or optinal id
 * @param aditionalId id to contact (optional)
 * @param id (optional) if it is empty generates randomnumber
 */
export const generateComponentId = (
  componentName: string,
  aditionalId = '',
  id: number = giveRandomNumber(1, 999),
): string => `${componentName}-${id}${aditionalId ? `-${aditionalId}` : ''}`;

type OperationsType = {
  type: string;
  method: (...args: unknown[]) => unknown;
};

export type ObjectToTransformValue =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | Array<unknown>
  | null
  | undefined;
/**
 * applyTransform2Object, function recursiva que aplica una transformación a los valores de un objeto,
 * en función del tipo de cada uno de esos valores
 * @param {array} valueOperations Array<OperationsType> que define que transformación aplicar por tipo
 * @param {Function} keyOperation Funcion que se le aplicará a las keys del objeto
 * @param {object} input Objeto al que se le desea aplicar transformaciones
 */
export const applyTransform2Object = (
  valueOperations: Array<OperationsType>,
  keyOperation = (keyName: string) => keyName,
  input: Record<string, ObjectToTransformValue> | ObjectToTransformValue,
): Record<string, ObjectToTransformValue> | ObjectToTransformValue => {
  return applyTransform2ObjectRecursivity(
    valueOperations,
    keyOperation,
    input,
  ) as Record<string, ObjectToTransformValue>;
};

const applyTransform2ObjectRecursivity = (
  valueOperations: Array<OperationsType>,
  keyOperation = (keyName: string) => keyName,
  input: unknown,
): unknown => {
  const isAnObject =
    input && typeof input === 'object' && !(input instanceof RegExp);

  const myInput = isAnObject ? input : {};
  const keys = Object.keys(myInput);
  const res = isAnObject ? {} : input;

  keys.forEach((key) => {
    let currentValue = myInput[key];
    const newKey = keyOperation(key);
    const currentValueType = typeof currentValue;
    const handler = valueOperations.find(
      (item) => item.type === currentValueType,
    );
    if (
      currentValue &&
      currentValueType !== 'object' &&
      currentValueType !== 'function' &&
      handler &&
      handler.method
    ) {
      currentValue = handler.method(currentValue);
    }
    if (
      currentValueType === 'undefined' &&
      handler &&
      handler.type === 'undefined'
    ) {
      currentValue = handler.method(currentValue);
    }

    if (currentValueType === 'object' && !Array.isArray(currentValue)) {
      res[newKey] = applyTransform2ObjectRecursivity(
        valueOperations,
        keyOperation,
        currentValue,
      );
    } else if (currentValueType === 'object' && Array.isArray(currentValue)) {
      res[newKey] = currentValue.map((arrayItem) => {
        return applyTransform2ObjectRecursivity(
          valueOperations,
          keyOperation,
          arrayItem,
        );
      });
    } else {
      res[newKey] = currentValue;
    }
  });

  return res;
};

/**
 * myAssign
 * @param {object} obj object where you desire to assing something (it does not matter is empty or not)
 * @param {string} keyPathDotNotation path inside object whete you want to place it like: info.data already splited by .
 * @param {string||number} value value to associate
 * @returns {object} for example: {href: 'https://www.movistar.es', referrer: 'https://www.google.com'}
 */
export const myAssign = (
  sourceObj: Record<string, unknown>,
  targetObj: Record<string, unknown>,
): Record<string, unknown> => {
  const res = Object.assign(sourceObj, targetObj);
  return res;
};

/**
 * cleanUnAllowedKeysInsideObject
 * @param {object} object2Clean object to clean
 * @param {Array<RegExp>} allowedKeys array of RegEx like: allowed: []
 * @returns {object} cleaned structure
 */
export const cleanUnAllowedKeysInsideObject = (
  object2Clean: Record<string, unknown>,
  allowedKeys: Array<RegExp>,
): Record<string, unknown> => {
  const newObjectCleaned = { ...object2Clean };
  // looping
  const keys = Object.keys(object2Clean);

  keys.forEach((key) => {
    let allowed = false;

    allowedKeys.forEach((regItem) => {
      const regEx = new RegExp(regItem);

      if (regEx.test(key) && !allowed) {
        allowed = true;
      }
    });

    if (!allowed) {
      delete newObjectCleaned[key];
    }
  });

  return newObjectCleaned;
};

/**
 * makeUpObj
 * @param {object} objRes object used to write the final result
 * @param {string} key path inside object whete you want to place it like: info.data
 * @param {string||number} value value to associate
 * @returns {object} for example: {href: 'https://www.movistar.es', referrer: 'https://www.google.com'}
 */
export const makeUpObj = (
  key: string,
  value: unknown,
): Record<string, unknown> => {
  const obj = {};
  const parts = key.split('.');
  if (parts.length === 1) {
    obj[parts[0]] = value;
  } else if (parts.length > 1) {
    // concat all but the first part of the key
    const remainingParts = parts.slice(1, parts.length).join('.');
    obj[parts[0]] = makeUpObj(remainingParts, value);
  }
  return obj;
};

/* Get all params on the current url like ?prod=producto&test="ok" */
// groupByObject means if keys like context.subseg and context.priority are found
// those will be group inside context object like: { priority, subseg }
// allowedParams (ARRAY OF REGEX like  [/prodCode/g ]) it will helps to clean unwanted params.
/**
 * getUrlParams
 * @param {bool} groupByObject object used to write the final result
 * @param {array} allowedKeys array of RegEx like: allowed: []
 * @returns {object} for example: {href: 'https://www.movistar.es', referrer: 'https://www.google.com'}
 */
export const getUrlParams = (
  groupByObject = false,
  allowedParams: Array<RegExp> = null,
): Record<string, unknown> => {
  let params = null;

  try {
    // detecting if we have # has router
    const hashInfo = !!window.location.hash;
    const searchParams = !!window.location.search;
    const hashRouterParasm =
      hashInfo && !searchParams && window.location.hash.indexOf('?') > -1;

    if (hashRouterParasm) {
      const beginPosition = window.location.hash.indexOf('?');
      const string2Analise = window.location.hash.substr(beginPosition);

      params = queryString.parse(string2Analise);
    } else {
      params = queryString.parse(window.location.search);
    }
  } catch (err) {
    errorLog(`Error on [getUrlParams] in jsUtils file: ${err}`);
  }
  // cleaning function
  if (Array.isArray(allowedParams) && params) {
    params = cleanUnAllowedKeysInsideObject(params, allowedParams);
  }
  // groupby . as object
  if (groupByObject) {
    let groupuedParams = {};
    const paramsKeys = Object.keys(params);

    paramsKeys.forEach((paramsKey) => {
      const value = params[paramsKey];

      if (paramsKey.indexOf('.') !== -1) {
        const auxObj = makeUpObj(paramsKey, value);
        groupuedParams = myAssign(groupuedParams, auxObj);
      } else {
        groupuedParams[paramsKey] = value;
      }
    });
    params = groupuedParams;
  }

  return params;
};

/**
 * Convierte a kebab case una cadena.
 * @param {string} cadena a transformar.
 * @returns {string} myExample --> my-example
 */
export const toKebabCase = (entry = ''): string => {
  let kebapName = entry
    .replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2')
    .toLowerCase();
  if (kebapName[0] === '-') {
    kebapName = kebapName.substr(1);
  }
  return kebapName;
};

/**
 * NOrmaliza un string, generalmente para el envio de metrics
 * @param {string} str a transformar.
 * @returns {string} myExample --> my-example
 */
export const normalizeString = (str: string): string => {
  let newStr = str;
  newStr = newStr.toLowerCase();
  newStr = newStr.replace(/á/g, 'a');
  newStr = newStr.replace(/é/g, 'e');
  newStr = newStr.replace(/í/g, 'i');
  newStr = newStr.replace(/ó/g, 'o');
  newStr = newStr.replace(/ú/g, 'u');
  newStr = newStr.replace(/ /g, '-');
  newStr = newStr.replace(/ñ/g, 'n');
  return newStr;
};

/**
 * Return immutable update of one element on received array
 * @param {array} array - Original array
 * @param {number} index - Index of new item values on received array
 * @param {object} newItemValues - New item values
 */
export const updateObjectInArray = (
  array: Array<Record<string, unknown>>,
  itemIndex: number,
  newItemValues: Record<string, unknown>,
): Array<Record<string, unknown>> => {
  return array.map((item, index) => {
    if (index !== itemIndex) {
      return item;
    }
    return {
      ...item,
      ...newItemValues,
    };
  });
};

/**
 * Return immutable addition of one element on received array
 * @param {array} array - Original array
 * @param {number} index - Position where newItem will be added
 * @param {object} newItem - New item of array
 */
export const insertItemInArray = (
  array: Array<Record<string, unknown>>,
  index: number,
  newItem: Record<string, unknown>,
): Array<Record<string, unknown>> => {
  const newArray = array.slice();
  newArray.splice(index, 0, newItem);
  return newArray;
};

/**
 * Return immutable addition of multiple items
 * @param {array} array - Original array
 * @param {array} newItems - Array of new items
 */
export const insertItemsInArray = (
  array: Array<Record<string, unknown>>,
  newItems: Array<Record<string, unknown>>,
): Array<Record<string, unknown>> => {
  const newArray = array.slice();
  newItems.forEach((item) => {
    newArray.splice(newArray.length, 0, item);
  });
  return newArray;
};
/**
 * getViewPortSize: get viewport size
 * @returns {number, number} width height
 */
export const getViewPortSize = (): {
  viewPortWidth: number;
  viewPortHeight: number;
} => {
  let viewPortWidth;
  let viewPortHeight;
  let clientWidth = 1000;
  let clientHeight = 600;

  try {
    ({ clientWidth } = document.getElementsByTagName('body')[0]);
    ({ clientHeight } = document.getElementsByTagName('body')[0]);
  } catch (err) {
    // we have tryied get values
  }
  /* the more standards compliant browsers (mozilla/netscape/opera/IE7)
  use window.innerWidth and window.innerHeight */
  if (window.innerWidth) {
    viewPortWidth = window.innerWidth;
    viewPortHeight = window.innerHeight;
  }
  // IE6 in standards compliant mode (i.e. with a valid doctype as the first line in the document)
  else if (
    document.documentElement &&
    document.documentElement.clientWidth &&
    document.documentElement.clientWidth !== 0
  ) {
    viewPortWidth = document.documentElement.clientWidth;
    viewPortHeight = document.documentElement.clientHeight;
  } else {
    viewPortWidth = clientWidth;
    viewPortHeight = clientHeight;
  }

  return { viewPortWidth, viewPortHeight };
};
/** getKindDeviceByViewportSize
 * @param {array} array - Original array
 * @param {array} newItems - Array of new items
 */
export const getDeviceKindDependsOnViewPort = (): string => {
  const { viewPortWidth } = getViewPortSize();
  let res = DEVICES.MOBILE;
  if (viewPortWidth >= 980) {
    res = DEVICES.PC;
  }

  if (viewPortWidth >= 768 && viewPortWidth < 980) {
    res = DEVICES.TABLET;
  }
  return res;
};
/**
 * setUpPropsByDe: Mergea las propiedades de desktop y/o tablet basándose en el device del cliente
 * @param {object} props - Original array
 * @returns {object} newItemValues - New item values
 */

export const setUpPropsByDevice = (
  props: Record<string, unknown>,
): Record<string, unknown> => {
  let result: Record<string, unknown> = { ...props };
  const { desktopProps, tabletProps } = result;
  if (props && (desktopProps || tabletProps)) {
    const device = getDeviceKindDependsOnViewPort();
    if (device && device !== DEVICES.MOBILE) {
      if (device === DEVICES.PC) {
        if (tabletProps) {
          result = merge(result, tabletProps);
        }
        if (desktopProps) {
          result = merge(result, desktopProps);
        }
      } else if (device === DEVICES.TABLET) {
        if (tabletProps) {
          result = merge(result, tabletProps);
        }
      }
    }
  }
  if (result.desktopProps) {
    delete result.desktopProps;
  }
  if (result.tabletProps) {
    delete result.tabletProps;
  }
  return result;
};
/**
 * Method that searches for the key corresponding to a certain value
 * @param {object} object - Object to make search
 * @param {any} value - Value to search his key
 */
export const getPropKeyByValue = (
  object: Record<string, unknown>,
  value: unknown,
): string => {
  let res = null;
  if (typeof object === 'object' && value) {
    res = Object.keys(object).find((key) => object[key] === value);
  } else {
    errorLog(
      '{getPropKeyByValue}: Object undefined or not received value to search',
      'getPropKeyByValue1',
    );
  }
  return res;
};
/**
 * getDeviceInfo: Detect info about where and how the page is executed
 * output example:
 * {
 *  "os":{"name":"Windows","version":10},
 *  "browser":{"name":"Chrome","version":80.03987149},
 *  "navigator":{
 *    "userAgent":"Mozilla/5.0 (Windows NT 10.0; Win64; x64)
 *     AppleWebKit/537.36 (KHTML, like Gecko) Chrome/80.0.3987.149 Safari/537.36",
 *    "appVersion":"5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko)
 *     Chrome/80.0.3987.149 Safari/537.36",
 *    "platform":"Win32","vendor":"Google Inc."
 *   }
 * }
 * @returns {object} for example: {href: 'https://www.movistar.es', referrer: 'https://www.google.com'}
 */
export const getDeviceInfo = (): Record<string, unknown> => {
  const module = {
    options: [],
    header: [
      navigator.platform,
      navigator.userAgent,
      navigator.appVersion,
      navigator.vendor,
    ],
    dataos: [
      { name: 'Windows Phone', value: 'Windows Phone', version: 'OS' },
      { name: 'Windows', value: 'Win', version: 'NT' },
      { name: 'iPhone', value: 'iPhone', version: 'OS' },
      { name: 'iPad', value: 'iPad', version: 'OS' },
      { name: 'Kindle', value: 'Silk', version: 'Silk' },
      { name: 'Android', value: 'Android', version: 'Android' },
      { name: 'PlayBook', value: 'PlayBook', version: 'OS' },
      { name: 'BlackBerry', value: 'BlackBerry', version: '/' },
      { name: 'Macintosh', value: 'Mac', version: 'OS X' },
      { name: 'Linux', value: 'Linux', version: 'rv' },
      { name: 'Palm', value: 'Palm', version: 'PalmOS' },
    ],
    databrowser: [
      { name: 'Chrome', value: 'Chrome', version: 'Chrome' },
      { name: 'Firefox', value: 'Firefox', version: 'Firefox' },
      { name: 'Safari', value: 'Safari', version: 'Version' },
      { name: 'Internet Explorer', value: 'MSIE', version: 'MSIE' },
      { name: 'Opera', value: 'Opera', version: 'Opera' },
      { name: 'BlackBerry', value: 'CLDC', version: 'CLDC' },
      { name: 'Mozilla', value: 'Mozilla', version: 'Mozilla' },
    ],
  };

  const matchItem = (string, data) => {
    let i = 0;
    let j = 0;
    let regex;
    let regexv;
    let match;
    let matches;
    let version;
    for (i = 0; i < data.length; i += 1) {
      regex = new RegExp(data[i].value, 'i');
      match = regex.test(string);
      if (match) {
        regexv = new RegExp(`${data[i].version}[- /:;]([\\d._]+)`, 'i');
        matches = string.match(regexv);
        version = '';
        if (matches) {
          if (matches[1]) {
            matches = matches[1];
          }
        }
        if (matches) {
          matches = matches.split(/[._]+/);
          for (j = 0; j < matches.length; j += 1) {
            if (j === 0) {
              version += `${matches[j]}.`;
            } else {
              version += matches[j];
            }
          }
        } else {
          version = '0';
        }
        return {
          name: data[i].name,
          version: parseFloat(version),
        };
      }
    }
    return { name: 'unknown', version: 0 };
  };

  const init = () => {
    const agent = module.header.join(' ');
    const os = matchItem(agent, module.dataos);
    const browser = matchItem(agent, module.databrowser);
    const navigatorData = {
      userAgent: navigator.userAgent,
      appVersion: navigator.appVersion,
      platform: navigator.platform,
      vendor: navigator.vendor,
    };
    return {
      os,
      browser,
      navigator: navigatorData,
    };
  };

  const info = init();
  return info;
};
/**
 * It gets cookie value using cookie name
 * @param cName cookie Name
 * @returns {string} cookie Value
 */
export const getCookie = (cName: string): string => {
  let cValue = document && document.cookie ? document.cookie : '';
  if (cValue) {
    let cStart = cValue.indexOf(` ${cName}=`);
    if (cStart === -1) {
      cStart = cValue.indexOf(`${cName}=`);
    }
    if (cStart === -1) {
      cValue = '';
    } else {
      cStart = cValue.indexOf('=', cStart) + 1;
      let cEnd = cValue.indexOf(';', cStart);
      if (cEnd === -1) {
        cEnd = cValue.length;
      }
      cValue = unescape(cValue.substring(cStart, cEnd));
    }
  }
  return cValue;
};

/**
 * setCookie: Set a cookie
 * @param cName name of cookie that we want to modify
 * @param value value that we want to set to the cookie
 * @param exdays available cookie time from this moment
 * @param domain domains where the cookie aplies
 * @param needScape boolean if we want to scape special characters
 * @returns {void}
 */
export const setCookie = (
  cName: string,
  value: string,
  exdays: number,
  domain = '',
  needScape = true,
): void => {
  const exdate = new Date();
  exdate.setDate(exdate.getDate() + exdays);
  let cValue = '';
  const parsedValue = needScape ? escape(value) : value;
  if (!domain) {
    cValue =
      parsedValue +
      (exdays === null ? '' : `;expires=${exdate.toUTCString()};path=/`);
  } else {
    cValue =
      parsedValue +
      (exdays === null
        ? ''
        : `;expires=${exdate.toUTCString()};domain=${domain};path=/`);
  }
  document.cookie = `${cName}=${cValue}`;
};

/**
 * deleteCookie: Delete a cookie
 * @param name name of the cookie that we want to delete
 * @returns {void}
 */
export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
};

/**
 * isIE11: check if current browser is IE11
 * @returns {boolean}
 */
export const isIE11 = (): boolean => {
  let res = false;
  if (
    navigator.userAgent.indexOf('MSIE') !== -1 ||
    navigator.appVersion.indexOf('Trident/') > -1
  ) {
    res = true;
  }
  return res;
};

/**
 * isSafariBrowser: check if current browser is Safari
 * @returns {boolean}
 */
export const isSafariBrowser = (): boolean => {
  let res = false;
  try {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf('safari') !== -1) {
      if (!(ua.indexOf('chrome') > -1)) {
        res = true;
      }
    }
  } catch (err) {
    /* console.log('something failed on this browser'); */
  }
  return res;
};

/**
 * normalizeTestVersion: give a proper number by testversion letter
 * @returns {number}
 */
 export const normalizeTestVersion = (versionTest = ''): number => {
  let res = 0;
  const versionTestNormalized = versionTest.toLowerCase();
  switch (versionTestNormalized) {
    case 'b':
      res = 1;
      break;
    case 'c':
      res = 2;
      break;
    default:
      res = 0;
      break;
  }
  return res;
};


export const isNumber = (value) => {
  return typeof value === 'number' && !isNaN(value);
}